import { Theme } from "@mui/material"
import { GlobalStyles, useTheme } from "@mui/system"
import { IRootState } from "app/model"
import { DuplicatePopup } from "campaign/components/list/DuplicatePopup"
import { InventoryMapFloatingPanel } from "form/map/components/InventoryMapFloatingPanel"
import React from "react"
import { connect } from "react-redux"
import { REPORTS_SORTABLE_ITEM_DEFAULT_CLASSNAME } from "reports/model"
import { getSortByItemSx } from "reports/tools/ui"
import { isDesktopScreen } from "ui/tools/responsive"
import { NotLogguedPage } from "user/components/NotLogguedPage"
import { SessionWillExpirePopup } from "user/components/SessionWillExpirePopup"
import { EMPTY_TOKEN_VALUE } from "user/model"
import { ColorPickerPopup } from "../ColorPickerPopup"
import { MainLayoutDesktop } from "./MainLayoutDesktop"
import { MainLayoutPhone } from "./MainLayoutPhone"

interface IMainLayoutProps {
  isLogged: boolean
  containerWidth: number // used to trigger a refresh for responsive UI
}

const MainLayoutFn = React.memo(({ isLogged }: IMainLayoutProps): JSX.Element => {
  const theme = useTheme()
  return (
    <>
      <GlobalStyles
        styles={{
          ".MuiButton-root": {
            textTransform: "initial",
            paddingTop: ".3rem",
            paddingBottom: ".3rem",
            paddingLeft: "1rem",
            paddingRight: "1rem"
          },
          "@keyframes fadeIn": {
            "0%": { opacity: 0 },
            "100%": { opacity: 1 }
          },
          "@keyframes fadeInTopDown": {
            "0%": { opacity: 0, transform: "translateY(-100%)" },
            "100%": { opacity: 1, transform: "translateY(0)" }
          },
          "@keyframes fadeInLeftRight": {
            "0%": { opacity: 0, transform: "translateX(-100%)" },
            "100%": { opacity: 1, transform: "translateX(0)" }
          },
          "@keyframes openLeftRight": {
            "0%": { transform: "translateX(-100%)" },
            "100%": { transform: "translateX(0)" }
          },
          "@keyframes rotateTopDown": {
            "0%": { transform: "rotate(180deg)" },
            "100%": { transform: "rotate(0deg)" }
          },
          "@keyframes shadowPulse": {
            "33%": {
              background: theme.palette.specialText,
              boxShadow: `-24px 0 ${theme.palette.special}, 24px 0 ${theme.palette.specialText}`
            },
            "66%": {
              background: theme.palette.special,
              boxShadow: `-24px 0 ${theme.palette.specialText}, 24px 0 ${theme.palette.specialText}`
            },
            "100%": {
              background: theme.palette.specialText,
              boxShadow: `-24px 0 ${theme.palette.specialText}, 24px 0 ${theme.palette.special}`
            }
          },
          ".mapboxgl-popup": {
            zIndex: 10
          },
          ".mapboxgl-popup-content": {
            backgroundColor: theme.palette.background.default
          },
          ".mapboxgl-popup-anchor-right .mapboxgl-popup-tip, .mapboxgl-popup-anchor-left .mapboxgl-popup-tip": {
            borderLeftColor: `${theme.palette.background.default} !important`,
            borderRightColor: `${theme.palette.background.default} !important`
          },
          ".mapboxgl-popup-anchor-top .mapboxgl-popup-tip, .mapboxgl-popup-anchor-bottom .mapboxgl-popup-tip": {
            borderTopColor: `${theme.palette.background.default} !important`,
            borderBottomColor: `${theme.palette.background.default} !important`
          },
          [`.${REPORTS_SORTABLE_ITEM_DEFAULT_CLASSNAME}`]: getSortByItemSx(theme as Theme),
          ".react-country-select input": {
            backgroundColor: theme.palette.background.default,
            color: theme.palette.text.primary,
            border: "none"
          },
          ".react-country-select .stdropdown-container": {
            border: theme.palette.border
          },
          ".react-country-select.error .stdropdown-container": {
            borderColor: theme.palette.error.main
          },
          ".react-country-select.error input::placeholder": {
            color: theme.palette.error.main
          },
          ".react-country-select .stdropdown-tool > svg": {
            filter: "invert(53%) sepia(69%) saturate(1958%) hue-rotate(330deg) brightness(92%) contrast(81%)"
          },
          ".stdropdown-menu": {
            backgroundColor: theme.palette.background.default,
            border: theme.palette.border,
            color: theme.palette.text.primary
          }
        }}
      />
      {!isLogged ? <NotLogguedPage /> : isDesktopScreen() ? <MainLayoutDesktop /> : <MainLayoutPhone />}
      <InventoryMapFloatingPanel />
      <DuplicatePopup />
      {isLogged && <SessionWillExpirePopup />}
      <ColorPickerPopup />
    </>
  )
})

export const MainLayout = connect(
  (state: IRootState): IMainLayoutProps => ({
    isLogged: state.user.token !== EMPTY_TOKEN_VALUE,
    containerWidth: state.ui.container.width
  })
)(MainLayoutFn)
